import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import logo from '../../../assets/images/farmstream_logo.png';
import { getErrorMessage } from '../../../utils';
import { useTradeRegisterMutation } from '../api/tradeApi';

export const TradeRegister = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [businessName, setBusinessName] = useState('');

  const [register, { isLoading: isRegistering }] = useTradeRegisterMutation();

  // If the backend is in TEST_MODE it returns the magic link directly so we can
  // skip the email round-trip and log straight in.
  const followMagicLink = (magicLink?: string) => {
    const token = magicLink?.split('/').pop();
    if (token) {
      navigate('/trade/login/' + token);
      return true;
    }
    return false;
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !businessName) return;
    try {
      const res = await register({ business_name: businessName, email }).unwrap();
      toast.success('Account created!');
      if (followMagicLink(res.magicLink)) return;
      toast.info('Check your email for a login link to access your portal.');
      navigate('/trade');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not create account'));
    }
  };

  return (
    <Background>
      <StyledImg src={logo} alt="Farmstream logo" />
      <Container>
        <Title>Register Your Business</Title>
        <Subtitle>
          Register your business to start referring customers and earning commission.
        </Subtitle>

        <Form onSubmit={handleRegister}>
          <Field>
            <Label>Business name</Label>
            <Input
              type="text"
              placeholder="Acme Installations Ltd"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              required
            />
          </Field>
          <Field>
            <Label>Email</Label>
            <Input
              type="email"
              placeholder="you@business.co.uk"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              required
            />
          </Field>
          <SubmitButton type="submit" disabled={isRegistering}>
            {isRegistering ? 'Creating…' : 'Create trade account'}
          </SubmitButton>
        </Form>

        <Divider>
          <span>Already registered?</span>
        </Divider>
        <LinkButton type="button" onClick={() => navigate('/trade')}>
          Sign in to your account
        </LinkButton>
      </Container>
      <BackLink href="https://www.farmstream.co.uk/">← Back to farmstream.co.uk</BackLink>
    </Background>
  );
};

const Background = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100dvh;
  background-color: ${({ theme }) => theme.colors.primary};
  padding: 1rem;
  gap: 1.5rem;
`;

const StyledImg = styled.img`
  max-width: 8rem;
`;

const Container = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;

  @media (max-width: 500px) {
    padding: 1.5rem;
    max-width: unset;
  }
`;

const Title = styled.h2`
  margin: 0;
  text-align: center;
  color: ${({ theme }) => theme.colors.gray900};
`;

const Subtitle = styled.p`
  margin: 0;
  text-align: center;
  color: #667085;
  font-size: 0.95rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const Label = styled.label`
  font-size: 0.85rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.gray900};
`;

const Input = styled.input`
  padding: 0.6rem;
  border-radius: 0.4rem;
  border: 1px solid ${({ theme }) => theme.colors.gray500};
  font-size: 1rem;
`;

const SubmitButton = styled.button`
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  padding: 0.75rem;
  border: none;
  border-radius: 0.4rem;
  cursor: pointer;
  font-weight: 500;
  font-size: 1rem;
  transition: opacity 0.2s;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Divider = styled.div`
  width: 100%;
  display: flex;
  gap: 1rem;
  align-items: center;
  font-size: 0.85rem;
  color: #999;
  margin: 0.5rem 0;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: #ddd;
  }
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  font-size: 0.95rem;
  text-decoration: underline;
  padding: 0;

  &:hover {
    opacity: 0.8;
  }
`;

const BackLink = styled.a`
  color: white;
  text-decoration: none;
  font-size: 0.9rem;

  &:hover {
    text-decoration: underline;
  }
`;
